import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart2,
  Users,
  DollarSign,
  TrendingUp,
  ArrowDownCircle,
  ArrowUpCircle,
  Activity,
  Briefcase,
  RefreshCw,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import Timeline from '../components/shared/Timeline';
import FundPerformanceChart from '../components/dashboard/FundPerformanceChart';
import InvestorGrowthChart from '../components/dashboard/InvestorGrowthChart';
import { pammDashboardService, pammFundService } from '../services/pammService';
import { formatCurrency, formatPercent } from '../utils';
import type { PAMMDashboardKPIs, PAMMFund } from '../types';
import type { TimelineEvent } from '../components/shared/Timeline';
import { PAMM_ROUTES } from '../../../constants/routeConstants';
import { MOCK_FUND_PERFORMANCE, MOCK_INVESTOR_GROWTH } from '../mock';

export default function PAMMDashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<PAMMDashboardKPIs | null>(null);
  const [funds, setFunds] = useState<PAMMFund[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [activity, setActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpiData, fundsData, activityData] = await Promise.all([
        pammDashboardService.getKPIs(),
        pammFundService.getAll(),
        pammDashboardService.getRecentActivity(),
      ]);
      setKpis(kpiData);
      setFunds(fundsData.slice(0, 5));
      setActivity(activityData);
    } catch {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const timelineEvents: TimelineEvent[] = activity.map(a => ({
    id: a.id,
    title: a.description,
    timestamp: a.timestamp,
    status: a.status,
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="PAMM Dashboard" />
        <LoadingSkeleton type="cards" cols={4} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LoadingSkeleton type="table" rows={4} />
          <LoadingSkeleton type="table" rows={4} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="PAMM Dashboard" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="PAMM Dashboard"
        description="Overview of your Percentage Allocation Money Management system"
      >
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </PageHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Funds"
          value={kpis?.totalFunds ?? 0}
          change={
            kpis?.totalFundsChange !== undefined
              ? formatPercent(kpis.totalFundsChange)
              : undefined
          }
          icon={Briefcase}
        />
        <StatsCard
          title="Total AUM"
          value={formatCurrency(kpis?.totalAUM ?? 0, 'USD', true)}
          change={
            kpis?.totalAUMChange !== undefined
              ? formatPercent(kpis.totalAUMChange)
              : undefined
          }
          icon={DollarSign}
        />
        <StatsCard
          title="Total Investors"
          value={kpis?.totalInvestors ?? 0}
          change={
            kpis?.totalInvestorsChange !== undefined
              ? formatPercent(kpis.totalInvestorsChange)
              : undefined
          }
          icon={Users}
        />
        <StatsCard
          title="Total Profit"
          value={formatCurrency(kpis?.totalProfit ?? 0, 'USD', true)}
          change={
            kpis?.totalProfitChange !== undefined
              ? formatPercent(kpis.totalProfitChange)
              : undefined
          }
          icon={TrendingUp}
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatsCard
          title="Total Deposits"
          value={formatCurrency(kpis?.totalDeposits ?? 0, 'USD', true)}
          icon={ArrowDownCircle}
        />
        <StatsCard
          title="Total Withdrawals"
          value={formatCurrency(kpis?.totalWithdrawals ?? 0, 'USD', true)}
          icon={ArrowUpCircle}
        />
        <StatsCard
          title="Active Trades"
          value={kpis?.activeTrades ?? 0}
          icon={Activity}
        />
        <StatsCard
          title="Pending Requests"
          value={kpis?.pendingRequests ?? 0}
          icon={BarChart2}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FundPerformanceChart
          data={MOCK_FUND_PERFORMANCE.data}
          title="Alpha Growth Fund — Performance"
        />
        <InvestorGrowthChart data={MOCK_INVESTOR_GROWTH} />
      </div>

      {/* Funds Table + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Recent Funds */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold">
              Active Funds
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => navigate(PAMM_ROUTES.PAMM_FUND_MANAGEMENT)}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Fund
                    </th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">
                      AUM
                    </th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Return
                    </th>
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {funds.map(fund => (
                    <tr
                      key={fund.id}
                      className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() =>
                        navigate(`${PAMM_ROUTES.PAMM_FUND_DETAILS}/${fund.id}`)
                      }
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground text-xs truncate max-w-[160px]">
                          {fund.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {fund.totalInvestors} investors
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell">
                        <span className="text-xs font-medium">
                          {formatCurrency(fund.totalAUM, fund.currency, true)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`text-xs font-semibold ${fund.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {formatPercent(fund.totalReturn)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusChip status={fund.status} size="sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            {timelineEvents.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                No recent activity
              </p>
            ) : (
              <Timeline events={timelineEvents} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
          <CardContent className="p-5">
            <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">
              Average Return
            </p>
            <p className="text-3xl font-bold text-blue-700 mt-1">
              {formatPercent(kpis?.averageReturn ?? 0)}
            </p>
            <p className="text-xs text-blue-500 mt-1">
              Across all active funds
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100">
          <CardContent className="p-5">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">
              Best Fund
            </p>
            <p className="text-lg font-bold text-emerald-700 mt-1 leading-tight">
              {kpis?.bestPerformingFund}
            </p>
            <p className="text-xs text-emerald-500 mt-1">
              Top performing fund this month
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-100">
          <CardContent className="p-5">
            <p className="text-xs font-medium text-violet-600 uppercase tracking-wide">
              Pending Requests
            </p>
            <p className="text-3xl font-bold text-violet-700 mt-1">
              {kpis?.pendingRequests ?? 0}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 h-6 text-xs text-violet-600 p-0 hover:bg-transparent"
              onClick={() => navigate(PAMM_ROUTES.PAMM_INVESTMENT_REQUESTS)}
            >
              Review Now →
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
